/**
 * Pre-RUM error buffer.
 *
 * Datadog RUM is initialized from a passive effect (`useRouteLogic` →
 * `loadGlobalVendors` → `initDatadog`), i.e. after hydration, and the SDK only
 * installs its `error` / `unhandledrejection` / `console.error` hooks once the
 * initial view starts (Next.js plugin = manual view tracking). Everything that
 * fires before that point — a chunk that throws on evaluation, a rejected promise
 * from a module initializer, and React hydration errors (#418/#419/#423, which
 * Next reports through `reportError` → window `error` event, falling back to
 * `console.error`) — never reached Datadog. Only chunk load failures had an
 * inline handler (app/layout.tsx).
 *
 * `bootErrorBufferScript` is a dependency-free inline script rendered first in
 * `<head>`, so it observes the whole document lifetime. It stores plain strings
 * (message / name / stack, clipped) into a bounded array on
 * `window[BOOT_ERROR_BUFFER_GLOBAL]`. `flushBootErrorBuffer` is called from
 * `initDatadog`'s onReady, right after the initial RUM view starts and the SDK
 * has attached its own hooks, so the seam is exact: the buffer stops taking
 * entries in the same tick the SDK starts, with no gap and no double capture.
 * Replayed errors land as `source:custom` with a `boot_buffer` context, and
 * `isFirstPartyError` (datadogInit.ts) runs them through the regular noise
 * filter instead of the addError bypass, since nothing pre-vetted them.
 *
 * Non-prod never calls `initDatadog`, so there the buffer just fills to its cap
 * and stays idle. console.error is wrapped (pass-through), never replaced back:
 * a later wrapper (Next dev overlay, the SDK itself) may sit on top of ours and
 * restoring the original would silently unhook it.
 */

export const BOOT_ERROR_BUFFER_GLOBAL = '__kalshiBootErrors';

/** Bounded so a tight error loop before init cannot grow memory unboundedly. */
export const BOOT_ERROR_BUFFER_MAX_ENTRIES = 20;
/** Per-string clip (message and stack each). RUM truncates further server-side. */
const MAX_TEXT_LENGTH = 4000;

export type BootErrorKind = 'error' | 'unhandledrejection' | 'console.error';

export interface BootErrorEntry {
    kind: BootErrorKind;
    message: string;
    name?: string;
    stack?: string;
    /** Unix ms at which the entry was captured. */
    occurred_at: number;
}

export interface BootErrorBufferSnapshot {
    entries: BootErrorEntry[];
    /** Entries discarded once `entries` hit the cap. */
    dropped: number;
}

interface BootErrorBufferHandle {
    /** Stops capturing and hands over everything captured so far. Idempotent. */
    take(): BootErrorBufferSnapshot;
}

const BOOT_ERROR_KINDS: readonly BootErrorKind[] = ['error', 'unhandledrejection', 'console.error'];

// Kept ES5 and self-contained on purpose: it ships in every document head and must
// run before any bundle. Errors thrown *by* this script would be the one thing it
// cannot report, so every path is guarded and falls back to console.warn (which the
// script does not wrap, so the fallback cannot recurse into the buffer).
export const bootErrorBufferScript = `
(function () {
    var MAX_ENTRIES = ${BOOT_ERROR_BUFFER_MAX_ENTRIES};
    var MAX_TEXT = ${MAX_TEXT_LENGTH};
    var entries = [];
    var dropped = 0;
    var active = true;

    function clip(value) {
        var text = String(value);
        return text.length > MAX_TEXT ? text.slice(0, MAX_TEXT) + ' [truncated]' : text;
    }
    // Error-like values keep name/message/stack; anything else becomes one message string.
    function describe(value) {
        if (value && typeof value === 'object' && typeof value.message === 'string') {
            return {
                message: value.message,
                name: typeof value.name === 'string' ? value.name : undefined,
                stack: typeof value.stack === 'string' ? value.stack : undefined
            };
        }
        if (typeof value === 'string') return { message: value };
        var text;
        try { text = JSON.stringify(value); } catch (e) { text = undefined; }
        return { message: typeof text === 'string' ? text : String(value) };
    }
    function push(kind, described) {
        if (!active) return;
        if (entries.length >= MAX_ENTRIES) { dropped++; return; }
        entries.push({
            kind: kind,
            message: clip(described.message),
            name: described.name,
            stack: described.stack ? clip(described.stack) : undefined,
            occurred_at: Date.now()
        });
    }
    // Bubble-phase window listener: only uncaught exceptions reach it (resource load
    // errors don't bubble), which is the same set the SDK's own runtime-error hook sees.
    function onError(event) {
        try {
            var described = event && event.error != null ? describe(event.error) : { message: (event && event.message) || 'Script error' };
            if (!described.stack && event && event.filename) {
                described.stack = described.message + '\\n    at ' + event.filename + ':' + event.lineno + ':' + event.colno;
            }
            push('error', described);
        } catch (e) { console.warn('[BootErrorBuffer] failed to record a pre-init error; it will be missing from RUM', e); }
    }
    function onRejection(event) {
        try {
            var reason = event && event.reason;
            // Falsy reasons carry zero attribution; the post-init filter drops them too.
            if (reason == null || reason === '') return;
            push('unhandledrejection', describe(reason));
        } catch (e) { console.warn('[BootErrorBuffer] failed to record a pre-init error; it will be missing from RUM', e); }
    }
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    var originalError = console.error;
    console.error = function () {
        if (active) {
            try {
                var parts = [];
                var firstWithStack;
                for (var i = 0; i < arguments.length; i++) {
                    var d = describe(arguments[i]);
                    if (!firstWithStack && d.stack) firstWithStack = d;
                    parts.push(d.name && d.stack ? d.name + ': ' + d.message : d.message);
                }
                push('console.error', {
                    message: parts.join(' '),
                    name: firstWithStack && firstWithStack.name,
                    stack: firstWithStack && firstWithStack.stack
                });
            } catch (e) { console.warn('[BootErrorBuffer] failed to record a pre-init error; it will be missing from RUM', e); }
        }
        return originalError.apply(console, arguments);
    };

    window[${JSON.stringify(BOOT_ERROR_BUFFER_GLOBAL)}] = {
        take: function () {
            active = false;
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onRejection);
            var snapshot = { entries: entries, dropped: dropped };
            entries = [];
            dropped = 0;
            return snapshot;
        }
    };
})();`;

function isBootErrorEntry(value: unknown): value is BootErrorEntry {
    if (!value || typeof value !== 'object') return false;
    const { kind, message, name, stack, occurred_at } = value as Partial<BootErrorEntry>;
    return (
        BOOT_ERROR_KINDS.includes(kind as BootErrorKind) &&
        typeof message === 'string' &&
        (name === undefined || typeof name === 'string') &&
        (stack === undefined || typeof stack === 'string') &&
        typeof occurred_at === 'number' &&
        Number.isFinite(occurred_at)
    );
}

/**
 * Rebuild an Error the SDK can parse a stack from. The original stack string is
 * reused verbatim so frames point at the code that threw, not at this replay.
 */
function toReplayError(entry: BootErrorEntry): Error {
    const error = new Error(entry.message);
    if (entry.name) error.name = entry.name;
    error.stack = entry.stack ?? `${error.name}: ${entry.message}`;
    return error;
}

/**
 * Call on the real SDK only after init AND the initial RUM view has started —
 * before that, the SDK has no error hooks of its own yet, so stopping the buffer
 * earlier would reopen the blind window it exists to close.
 */
export function flushBootErrorBuffer(addError: (error: Error, context: Record<string, unknown>) => void): void {
    const handle = (window as unknown as Record<string, BootErrorBufferHandle | undefined>)[BOOT_ERROR_BUFFER_GLOBAL];
    // Absent when the document was rendered without app/layout.tsx (Storybook, tests).
    if (!handle) return;

    let snapshot: BootErrorBufferSnapshot;
    try {
        snapshot = handle.take();
    } catch (error) {
        console.warn('[BootErrorBuffer] could not read pre-init errors; they will be missing from RUM', error);
        return;
    }

    const replayedAt = Date.now();
    for (const entry of snapshot.entries) {
        if (!isBootErrorEntry(entry)) {
            // The inline script and this module ship in the same document, so a shape
            // mismatch means the two have drifted.
            console.warn('[BootErrorBuffer] dropping malformed pre-init entry', { entry });
            continue;
        }
        addError(toReplayError(entry), {
            boot_buffer: {
                kind: entry.kind,
                occurred_at: entry.occurred_at,
                replay_delay_ms: Math.max(0, replayedAt - entry.occurred_at),
                dropped: snapshot.dropped,
            },
        });
    }
}
